import { X, Globe, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";

// Full-screen, in-app web viewer. Opens an external site inside the pinned app
// (kiosk-safe: no new tab/window) and keeps the guest contained — a Close button
// returns them to the guidebook. Some sites block embedding (X-Frame-Options/CSP);
// for those, a friendly message + QR fallback is shown.
export function InAppBrowser({ url, title, mode, distance, onClose }) {
  let host = url || "";
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
  const distanceDisplay = distance && /^\d+(\.\d+)?$/.test(String(distance).trim()) ? `${String(distance).trim()} miles` : distance;
  const isKiosk = typeof window !== "undefined" && (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );

  return (
    <AnimatePresence>
      {url && (
        <motion.div
          data-testid="inapp-browser"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex flex-col bg-brand-bg"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-brand-forest text-white shadow-md">
            <div className="flex min-w-0 items-center gap-2">
              <Globe size={18} className="shrink-0 text-brand-terracotta" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{title || host}</p>
                <p className="truncate text-xs text-white/60">{host}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                data-testid="inapp-browser-open"
                onClick={() => { const w = window.open(url, "_blank"); if (w) w.opener = null; }}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2.5 text-sm font-semibold text-white active:scale-95 transition-transform"
              >
                <ExternalLink size={16} /> Open
              </button>
              <button
                data-testid="inapp-browser-close"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-brand-forest active:scale-95 transition-transform"
              >
                <X size={16} /> Close
              </button>
            </div>
          </div>
          <div className="relative flex-1">
            {mode === "qr" ? (
              <div className="flex h-full flex-col items-center justify-center p-8 text-center bg-brand-bg">
                {distanceDisplay && (
                  <p data-testid="inapp-distance" className="text-brand-forest font-semibold text-lg sm:text-xl mb-6">
                    This place is <span className="text-brand-terracotta">{distanceDisplay}</span> from this location
                  </p>
                )}
                <div className="rounded-3xl bg-white p-6 shadow-lg border border-black/5">
                  <QRCodeSVG value={url} size={230} fgColor="#2C3B2E" bgColor="#ffffff" level="M" />
                </div>
                <h2 className="font-display text-3xl tracking-tight text-brand-forest mt-8">{title || "Scan with your phone"}</h2>
                <p className="text-brand-forest/60 mt-2 max-w-sm">Point your phone's camera at the code to open turn-by-turn directions in your maps app.</p>
              </div>
            ) : (
              <>
                <iframe
                  title={title || host}
                  src={url}
                  className="absolute inset-0 h-full w-full border-0 bg-white"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  referrerPolicy="no-referrer"
                />
                {/* Fallback for sites that refuse embedding — kiosk/pinned only */}
                {isKiosk && (
                  <div
                    data-testid="inapp-browser-qr"
                    className="absolute bottom-4 left-4 flex items-center gap-3 rounded-2xl border border-black/5 bg-white/95 backdrop-blur-sm px-4 py-3 shadow-lg"
                  >
                    <div className="rounded-lg bg-white p-1">
                      <QRCodeSVG value={url} size={64} fgColor="#2C3B2E" bgColor="#ffffff" level="M" />
                    </div>
                    <div className="max-w-[180px] text-left">
                      <p className="text-sm font-semibold text-brand-forest">Page not showing?</p>
                      <p className="text-xs text-brand-forest/60">Scan to open this site on your phone</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
